import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";

/* 成都公司（cd）以 ¥ 记账：录入 / 显示 ¥，折算 $ 计入总表 */
export const CNY_BRANCHES = ["cd"];
export const isCnyBranch = (b) => CNY_BRANCHES.includes(b);
export const isCnyRow = (r) => r?.currency === "CNY" || (isCnyBranch(r?.branch) && r?.amountCny != null);
export const cny = (n) => "¥" + Math.round(Number(n || 0)).toLocaleString();
export const usd = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
export const cur = (n, branch) => (isCnyBranch(branch) ? cny(n) : usd(n));
export const RELIABLE_FX = ["manual", "ecb", "cached"];
/* 成都行本币取值（单位安全）：stored=已存 ¥；derived=按记录级有效汇率 $×fxRate 派生；pending=无可靠 ¥/汇率（原币 $，不得当 ¥、不得作 0） */
export const cnyOf = (row, field = "amount", cnyField = "amountCny") => {
  if (row?.[cnyField] != null) return { cny: Number(row[cnyField]) || 0, kind: "stored", usd: Number(row?.[field]) || 0 };
  const u = Number(row?.[field]) || 0;
  const fx = Number(row?.fxRate) || 0;
  if (fx > 0 && RELIABLE_FX.includes(row?.fxSource)) return { cny: Math.round(u * fx * 100) / 100, kind: "derived", usd: u };
  return { cny: null, kind: "pending", usd: u };
};
/* 成都行合计：已知（已存 + 派生）¥ 小计 + 原币未定 $ 清单；pending>0 即为不完整 */
export const sumCny = (rows, field = "amount", cnyField = "amountCny") => rows.reduce((acc, r) => {
  const x = cnyOf(r, field, cnyField);
  if (x.kind === "pending") { acc.pendingCount += 1; acc.pendingUsd += x.usd; } else { acc.knownCny += x.cny; if (x.kind === "derived") acc.derivedCount += 1; }
  return acc;
}, { knownCny: 0, pendingCount: 0, pendingUsd: 0, derivedCount: 0 });

/* 金额单元：成都行显示 ¥ 主金额 + 折算 $ 小字；派生值标「派生」；原币未定行显示 $ 并标「未换算」；其他行显示 $ */
export function AmountCell({ row, field = "amount", cnyField = "amountCny", className = "", testid, precise = false }) {
  // 运营费用需要逐笔核对 CEO 批准金额；保留到分，不能把 $96.58 显示成 $97。
  // 作为可选显示模式复用本币/汇率逻辑，其它既有概览保持原来的显示口径。
  const exact = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const showUsd = precise ? (n) => "$" + exact(n) : usd;
  const showCny = precise ? (n) => "¥" + exact(n) : cny;
  if (!isCnyBranch(row?.branch)) return <span className={className} data-testid={testid}>{showUsd(row?.[field])}</span>;
  const x = cnyOf(row, field, cnyField);
  if (x.kind === "pending") {
    return (
      <span className={`inline-flex flex-col items-end leading-tight ${className}`} data-testid={testid} data-currency="USD" data-cny="pending">
        <span>{showUsd(x.usd)}</span>
        <span className="text-[10px] font-normal text-amber-600" title="无 ¥ 金额且无有效记录级汇率，未换算，不计入 ¥ 合计">原币 · 未换算</span>
      </span>
    );
  }
  return (
    <span className={`inline-flex flex-col items-end leading-tight ${className}`} data-testid={testid} data-currency="CNY" data-cny={x.kind}>
      <span>{showCny(x.cny)}{x.kind === "derived" && <span className="ml-1 text-[10px] font-normal text-indigo-600" title={`按记录级汇率 1 USD = ${row.fxRate} CNY（${row.fxSource}）派生`}>派生</span>}</span>
      <span className="text-[10px] font-normal text-slate-400" title={row?.fxRate ? `1 USD = ${row.fxRate} CNY${row.fxSource === "manual" ? "（手工）" : ""}` : ""}>≈ {showUsd(x.usd)}</span>
    </span>
  );
}

/* 录入表单里的实时汇率提示：按记账日期取 USD→CNY，显示折算金额 */
export function FxHint({ date, amount, zh, testid = "fx-hint" }) {
  const { api } = useApp();
  const [fx, setFx] = useState(null);
  const d = (date || "").slice(0, 10);
  useEffect(() => {
    let live = true;
    api.get("/finance/fx/usdcny", { params: d ? { date: d } : {} }).then(({ data }) => live && setFx(data)).catch(() => live && setFx(null));
    return () => { live = false; };
  }, [api, d]);
  if (!fx) return null;
  const amt = Number(amount) || 0;
  const src = fx.source === "manual" ? (zh ? "手工月汇率" : "manual") : fx.source === "ecb" ? (zh ? "欧洲央行参考价" : "ECB") : (zh ? "最近缓存" : "cached");
  return (
    <p className="col-span-2 text-[11px] text-teal-700 bg-teal-50 border border-teal-100 rounded-md px-2 py-1" data-testid={testid} data-rate={fx.rate}>
      {zh ? `成都公司以 ¥ 记账 · 按 ${fx.usedDate || d} 汇率 1 USD = ${fx.rate} CNY（${src}）` : `Chengdu books in CNY · ${fx.usedDate || d} rate 1 USD = ${fx.rate} CNY (${src})`}
      {amt > 0 && <b className="ml-1" data-testid={`${testid}-usd`}>{zh ? "折算" : "≈"} ${Math.round(amt / fx.rate).toLocaleString()}</b>}
      {zh ? " 计入总表" : " in consolidated totals"}
    </p>
  );
}
